import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import InlineOption from './inline-option';
import './styles.scss';

import MoreSelect from '../more-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons';

interface OptionType {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

interface GroupOptionType {
  value: string;
  label: string;
  icon?: React.ReactNode;
  items: (OptionType | GroupOptionType)[];
}

interface InlineOptionsProps {
  selectedOption?: string | string[];
  onOptionChange?: (option: string | string[]) => void;
  options: (OptionType | GroupOptionType)[];
  isMultipleSelect?: boolean;
  maxVisibleOptions?: number;
  isCalculateVisibleCount?: boolean;
  isFilter?: boolean;
  classname?: string;
  leftLabel?: string;
  optionDisabled?: boolean;
  // Opt-in: render every option as an inline chip that wraps to multiple lines,
  // skipping the width-collapse calc and the "more" overflow dropdown entirely.
  // Default false, so the width-collapse path is unchanged for every other
  // consumer. Used by the registry token selector so overflow tokens stay
  // directly clickable.
  disableOverflow?: boolean;
}

const InlineOptions: React.FC<InlineOptionsProps> = (props) => {
  const {
    selectedOption,
    onOptionChange,
    options,
    isMultipleSelect,
    maxVisibleOptions,
    isCalculateVisibleCount,
    isFilter,
    classname,
    leftLabel,
    optionDisabled = false,
    disableOverflow = false,
  } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState<number>(maxVisibleOptions || options.length);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const moreRef = useRef<HTMLDivElement>(null);

  const isOptionSelected = (
    selected: string | string[] | undefined,
    option: string,
    multiple: boolean | undefined,
  ) => {
    if (multiple) {
      return Array.isArray(selected) && selected.includes(option);
    }
    return selected === option;
  };

  const getUpdatedSelection = (selected: string | string[] | undefined, option: string) => {
    const selectedArray = Array.isArray(selected) ? selected : [];
    return selectedArray.includes(option)
      ? selectedArray.filter((item) => item !== option)
      : [...selectedArray, option];
  };

  const handleOptionClick = (option: string) => {
    if (isMultipleSelect) {
      onOptionChange?.(getUpdatedSelection(selectedOption, option));
    } else {
      onOptionChange?.(option);
    }
  };

  const getIcon = (option: OptionType) => {
    if (isMultipleSelect && isOptionSelected(selectedOption, option.value, isMultipleSelect)) {
      return <FontAwesomeIcon icon={faCheck} className="multiple-icon" />;
    }

    return option.icon;
  };

  useEffect(() => {
    if (!containerRef.current || !isCalculateVisibleCount || disableOverflow) return;

    const calculateVisibleCount = () => {
      const container = containerRef.current;
      if (!container) return;

      const containerWidth = container.offsetWidth;
      let total = 0;
      let count = 0;
      const maxCount =
        typeof maxVisibleOptions === 'number'
          ? Math.min(maxVisibleOptions, options.length)
          : options.length + 1;

      const GAP = 4;
      for (let i = 0; i < options.length && count < maxCount; i++) {
        const el = optionRefs.current[i];
        if (!el) continue;
        if (count > 0) {
          total += GAP;
        }
        total += el.offsetWidth;

        const hasMore = i < options.length - 1 && count < maxCount - 1;
        let moreWidth = 0;
        if (hasMore && moreRef.current) {
          moreWidth = moreRef.current.offsetWidth;
        }
        if (total + moreWidth > containerWidth) {
          break;
        }
        count++;
      }

      setVisibleCount(count);
    };

    calculateVisibleCount();

    const ro = new ResizeObserver(() => {
      calculateVisibleCount();
    });
    ro.observe(containerRef.current);

    window.addEventListener('resize', calculateVisibleCount);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', calculateVisibleCount);
    };
  }, [options]);

  // In wrap mode every option is inline and the overflow dropdown is suppressed,
  // so the width-collapse count is ignored and moreOptions is always empty.
  const effectiveVisibleCount = disableOverflow ? options.length : visibleCount;
  const visibleOptions = options.slice(0, effectiveVisibleCount);
  const moreOptions = disableOverflow ? [] : options.slice(effectiveVisibleCount);

  const renderHiddenInlineOptions = () => (
    <div
      style={{
        position: 'absolute',
        visibility: 'hidden',
        height: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
      }}
    >
      {options.map((option, idx) => (
        <div
          key={option.value}
          ref={(el) => (optionRefs.current[idx] = el)}
          style={{ display: 'inline-flex' }}
        >
          <InlineOption
            value={option.value}
            label={option.label}
            iconLeft={option.icon}
            selected={isOptionSelected(selectedOption, option.value, isMultipleSelect)}
            onClick={handleOptionClick}
            key={option.value}
          />
        </div>
      ))}
      <div ref={moreRef} style={{ display: 'inline-flex' }}>
        <MoreSelect
          options={moreOptions.map((opt) => ({
            value: opt.value,
            label: opt.label,
            iconLeft: opt.icon,
          }))}
          selectedValues={
            Array.isArray(selectedOption) ? selectedOption : selectedOption ? [selectedOption] : []
          }
          onChange={onOptionChange as (values: string[]) => void}
        />
      </div>
    </div>
  );

  useEffect(() => {
    if (!maxVisibleOptions) {
      setVisibleCount(options.length);
    }
  }, [options, setVisibleCount]);

  return (
    <div
      className={classNames('inline-options-container', classname || '', {
        'inline-options-calculate-visible': isCalculateVisibleCount && !disableOverflow,
        'inline-options-wrap': disableOverflow,
      })}
      ref={containerRef}
    >
      {leftLabel && <div className="inline-options-left-label">{leftLabel}</div>}
      {isCalculateVisibleCount && !disableOverflow && renderHiddenInlineOptions()}
      {visibleOptions.map((option) => (
        <div
          key={option.value}
          style={{ display: 'inline-flex', pointerEvents: optionDisabled ? 'none' : 'auto' }}
        >
          <InlineOption
            value={option.value}
            label={option.label}
            iconLeft={getIcon(option)}
            selected={isOptionSelected(selectedOption, option.value, isMultipleSelect)}
            onClick={handleOptionClick}
            isFilter={isFilter}
          />
        </div>
      ))}
      {moreOptions.length > 0 && (
        <div style={{ display: 'inline-flex' }}>
          <MoreSelect
            options={moreOptions}
            selectedValues={
              Array.isArray(selectedOption)
                ? selectedOption
                : selectedOption
                  ? [selectedOption]
                  : []
            }
            onChange={onOptionChange as (values: string[]) => void}
          />
        </div>
      )}
    </div>
  );
};

export default InlineOptions;
